import React, { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Row
} from 'reactstrap';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import AdminHeader from '../../../components/header/AdminHeader';
import {
  pm2Action,
  pm2RestartAll,
  serverMetricsGet,
  serverServicesGet,
  serviceAction,
  systemAction
} from '../../../store/slices/adminSlices/serverHealthSlice';

const REFRESH_MS = 5000;

// ---- formatters ----
const fmtBytes = (bytes, decimals = 1) => {
  if (!bytes || bytes <= 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
};

const fmtRate = (bytesPerSec) => `${fmtBytes(bytesPerSec)}/s`;

const fmtUptime = (sec) => {
  if (!sec && sec !== 0) return '-';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(' ');
};

// 80%+ => red (danger), 60-79% => warning, else green.
const usageColor = (p) => (p >= 80 ? 'danger' : p >= 60 ? 'warning' : 'success');

const StatCard = ({ icon, title, percent, primary, secondary }) => (
  <Card className="p-3 h-100 shadow-sm border-0">
    <div className="d-flex align-items-center justify-content-between mb-2">
      <span className="text-muted fw-medium d-flex align-items-center gap-2">
        {icon} {title}
      </span>
      {percent !== undefined && (
        <Badge color={usageColor(percent)} pill className="fs-6">
          {percent}%
        </Badge>
      )}
    </div>
    <h3 className="fw-bolder mb-1">{primary}</h3>
    <small className="text-muted">{secondary}</small>
    {percent !== undefined && (
      <Progress
        value={percent}
        color={usageColor(percent)}
        className="mt-2"
        style={{ height: '6px' }}
      />
    )}
  </Card>
);

StatCard.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string,
  percent: PropTypes.number,
  primary: PropTypes.node,
  secondary: PropTypes.node
};

const ServerHealth = () => {
  const dispatch = useDispatch();

  const [metrics, setMetrics] = useState(null);
  const [servicesData, setServicesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const timerRef = useRef(null);

  // password-gated action flow
  const [pendingAction, setPendingAction] = useState(null);
  const [pwInput, setPwInput] = useState('');
  const [pwSubmitting, setPwSubmitting] = useState(false);
  // services currently mid-restart (shown as "Restarting…" until they're back up)
  const [restarting, setRestarting] = useState({});

  const fetchAll = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [m, s] = await Promise.all([
        dispatch(serverMetricsGet()).unwrap(),
        dispatch(serverServicesGet()).unwrap()
      ]);
      if (m?.success) setMetrics(m.data);
      if (s?.success) setServicesData(s.data);
    } catch (error) {
      console.error('server health fetch error', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    clearInterval(timerRef.current);
    // Only poll while auto-refresh is on AND the tab is visible — no background load.
    if (autoRefresh && !document.hidden) {
      timerRef.current = setInterval(() => fetchAll(true), REFRESH_MS);
    }
    return () => clearInterval(timerRef.current);
  }, [autoRefresh]);

  // Pause polling when the tab is hidden; resume (with a fresh fetch) when visible.
  useEffect(() => {
    const onVisibility = () => {
      clearInterval(timerRef.current);
      if (autoRefresh && !document.hidden) {
        fetchAll(true);
        timerRef.current = setInterval(() => fetchAll(true), REFRESH_MS);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [autoRefresh]);

  // NOTE: we intentionally do NOT clear the "Restarting…" flag the moment a service
  // reports "up". A service like FreeSWITCH stays up for a second or two before
  // systemctl actually stops it, so clearing early would make it flash "Stopped"
  // mid-restart. The flag is cleared only by the fixed timeout set when restarting,
  // and the display already shows "Running" while up within that window.

  // open the password modal for a control action
  const askPassword = (cfg) => {
    setPendingAction(cfg);
    setPwInput('');
  };

  const runPendingAction = async () => {
    if (!pendingAction || !pwInput) return;
    const cfg = pendingAction;
    try {
      setPwSubmitting(true);
      setBusyKey(cfg.busyKey);
      let res;
      if (cfg.type === 'pm2') {
        res = await dispatch(pm2Action({ ...cfg.payload, password: pwInput })).unwrap();
      } else if (cfg.type === 'pm2-all') {
        res = await dispatch(pm2RestartAll({ password: pwInput })).unwrap();
      } else if (cfg.type === 'service') {
        res = await dispatch(serviceAction({ ...cfg.payload, password: pwInput })).unwrap();
      } else if (cfg.type === 'system') {
        res = await dispatch(systemAction({ ...cfg.payload, password: pwInput })).unwrap();
      }
      if (res?.success) {
        toast.success(res.message);
        setPendingAction(null);
        setPwInput('');
        // Mark a service as "Restarting…" so it doesn't flash red while it cycles.
        if (cfg.type === 'service' && cfg.payload?.key) {
          const k = cfg.payload.key;
          setRestarting((prev) => ({ ...prev, [k]: true }));
          // Hold "Restarting…" for the whole restart window (FreeSWITCH can take ~20-30s).
          // Within this window: down => "Restarting…", up => "Running".
          setTimeout(() => {
            setRestarting((prev) => {
              const next = { ...prev };
              delete next[k];
              return next;
            });
          }, 30000);
        }
        if (cfg.type !== 'system') {
          // pm2-all also restarts the API itself, so give it longer to come back.
          setTimeout(() => fetchAll(true), cfg.type === 'pm2-all' ? 4500 : 1500);
        }
      }
    } catch (error) {
      // error toast handled by axios interceptor; keep modal open to retry
      console.error(error);
    } finally {
      setPwSubmitting(false);
      setBusyKey(null);
    }
  };

  const host = metrics?.host;
  const cpu = metrics?.cpu;
  const mem = metrics?.memory;
  const disks = metrics?.disks || [];
  const network = metrics?.network || [];
  const processes = metrics?.processes || { byCpu: [], byMem: [] };
  // Only system-level services (those with a `unit`) are shown for now.
  const services = (servicesData?.services || []).filter((s) => s.unit);

  const rootDisk = disks.find((d) => d.mount === '/') || disks[0];

  return (
    <div>
      <AdminHeader title="Server Health" />

      {/* Top bar: hostname + uptime + system controls */}
      <div className="d-flex align-items-center justify-content-between px-1 mb-3 flex-wrap gap-2">
        <div className="text-muted">
          {host ? (
            <>
              <Icon.Clock size={15} className="me-1" />
              System Uptime: <span className="fw-medium text-primary">{fmtUptime(host.uptime)}</span>
            </>
          ) : (
            'Loading server info…'
          )}
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Button
            size="sm"
            color={autoRefresh ? 'success' : 'secondary'}
            outline
            onClick={() => setAutoRefresh((v) => !v)}>
            {autoRefresh ? <Icon.Pause size={15} /> : <Icon.Play size={15} />}
            <span className="ms-1">Auto {autoRefresh ? 'On' : 'Off'}</span>
          </Button>
          <Button size="sm" color="primary" outline onClick={() => fetchAll()}>
            <Icon.RefreshCw size={15} /> <span className="ms-1">Refresh</span>
          </Button>
          {
            <>
              <Button
                size="sm"
                color="warning"
                onClick={() =>
                  askPassword({
                    type: 'system',
                    payload: { action: 'reboot' },
                    busyKey: 'sys:reboot',
                    title: 'Restart Server',
                    warning:
                      'This will REBOOT the entire server. All calls and services will go down for a few minutes.'
                  })
                }>
                <Icon.RefreshCcw size={15} /> <span className="ms-1">Restart Server</span>
              </Button>
              <Button
                size="sm"
                color="danger"
                onClick={() =>
                  askPassword({
                    type: 'system',
                    payload: { action: 'shutdown' },
                    busyKey: 'sys:shutdown',
                    title: 'Shutdown Server',
                    warning:
                      'This will POWER OFF the server. It will stay down until someone powers it on physically.'
                  })
                }>
                <Icon.Power size={15} /> <span className="ms-1">Shutdown</span>
              </Button>
            </>
          }
        </div>
      </div>

      {loading && !metrics ? (
        <Card className="p-5 text-center text-muted">
          <Icon.Loader className="mb-2" /> Loading server metrics…
        </Card>
      ) : (
        <>
          {/* Overview cards */}
          <Row className="g-3 mb-1">
            <Col xl="3" md="6">
              <StatCard
                icon={<Icon.Cpu size={18} />}
                title="CPU Load"
                percent={cpu?.usagePercent}
                primary={`${cpu?.usagePercent ?? 0}%`}
                secondary={`${cpu?.cores ?? 0} cores • load ${host?.loadavg?.join(' / ') ?? '-'}`}
              />
            </Col>
            <Col xl="3" md="6">
              <StatCard
                icon={<Icon.Server size={18} />}
                title="Memory (RAM)"
                percent={mem?.usedPercent}
                primary={`${fmtBytes(mem?.used)} / ${fmtBytes(mem?.total)}`}
                secondary={`${fmtBytes(mem?.free)} available`}
              />
            </Col>
            <Col xl="3" md="6">
              <StatCard
                icon={<Icon.HardDrive size={18} />}
                title="Disk (/)"
                percent={rootDisk?.usedPercent}
                primary={`${fmtBytes(rootDisk?.used)} / ${fmtBytes(rootDisk?.size)}`}
                secondary={`${fmtBytes(rootDisk?.avail)} free on ${rootDisk?.mount ?? '/'}`}
              />
            </Col>
            <Col xl="3" md="6">
              <StatCard
                icon={<Icon.Activity size={18} />}
                title="Swap"
                percent={mem?.swapUsedPercent}
                primary={`${fmtBytes(mem?.swapUsed)} / ${fmtBytes(mem?.swapTotal)}`}
                secondary={`Server time ${host ? new Date(host.serverTime).toLocaleTimeString() : '-'}`}
              />
            </Col>
          </Row>

          <Row className="g-3 mt-0">
            {/* CPU per-core */}
            <Col lg="6">
              <Card className="p-3 h-100 shadow-sm border-0">
                <h6 className="fw-bolder mb-1 d-flex align-items-center gap-2">
                  <Icon.Cpu size={18} /> Processor
                </h6>
                <small className="text-muted d-block mb-3">
                  {cpu?.model} @ {cpu?.speedMhz} MHz
                </small>
                <Row className="g-2">
                  {(cpu?.perCore || []).map((c, i) => (
                    <Col xs="6" key={i}>
                      <div className="d-flex justify-content-between small">
                        <span className="text-muted">Core {i}</span>
                        <span className="fw-medium">{c}%</span>
                      </div>
                      <Progress value={c} color={usageColor(c)} style={{ height: '5px' }} />
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>

            {/* Disks + network */}
            <Col lg="6">
              <Card className="p-3 h-100 shadow-sm border-0">
                <h6 className="fw-bolder mb-3 d-flex align-items-center gap-2">
                  <Icon.HardDrive size={18} /> Storage
                </h6>
                {disks.map((d, i) => (
                  <div key={i} className="mb-3">
                    <div className="d-flex justify-content-between small">
                      <span className="fw-medium">{d.mount}</span>
                      <span className="text-muted">
                        {fmtBytes(d.used)} / {fmtBytes(d.size)} ({d.usedPercent}%)
                      </span>
                    </div>
                    <Progress
                      value={d.usedPercent}
                      color={usageColor(d.usedPercent)}
                      style={{ height: '6px' }}
                    />
                    <small className="text-muted">{d.filesystem}</small>
                  </div>
                ))}

                <h6 className="fw-bolder mt-2 mb-2 d-flex align-items-center gap-2">
                  <Icon.Wifi size={18} /> Network
                </h6>
                {network.map((n, i) => (
                  <div
                    key={i}
                    className="d-flex justify-content-between align-items-center small mb-1">
                    <span className="fw-medium">{n.iface}</span>
                    <span className="text-success">
                      <Icon.ArrowDown size={13} /> {fmtRate(n.rxRate)}
                    </span>
                    <span className="text-primary">
                      <Icon.ArrowUp size={13} /> {fmtRate(n.txRate)}
                    </span>
                    <span className="text-muted">
                      ↓{fmtBytes(n.rxTotal)} ↑{fmtBytes(n.txTotal)}
                    </span>
                  </div>
                ))}
              </Card>
            </Col>
          </Row>

          {/* Core services: green/red box, no port, restart (password protected) */}
          <Card className="p-3 mt-3 shadow-sm border-0">
            <h6 className="fw-bolder mb-3 d-flex align-items-center gap-2">
              <Icon.Database size={18} /> Core Services
            </h6>
            <Row className="g-3">
              {services.map((s) => (
                <Col xl="4" md="6" key={s.key}>
                  <div className="border rounded-3 p-3 d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                      <span
                        className={`rounded ${
                          restarting[s.key] && !s.up
                            ? 'bg-warning'
                            : s.up
                              ? 'bg-success'
                              : 'bg-danger'
                        }`}
                        style={{ width: 18, height: 18, display: 'inline-block' }}
                        title={restarting[s.key] && !s.up ? 'Restarting' : s.up ? 'Running' : 'Stopped'}
                      />
                      <div>
                        <div className="fw-bold">{s.name}</div>
                        <small
                          className={
                            restarting[s.key] && !s.up
                              ? 'text-warning'
                              : s.up
                                ? 'text-success'
                                : 'text-danger'
                          }>
                          {restarting[s.key] && !s.up
                            ? 'Restarting…'
                            : s.up
                              ? 'Running'
                              : 'Stopped'}
                          {s.up && s.extra ? ` • ${s.extra}` : ''}
                        </small>
                        {s.up && s.uptime ? (
                          <small className="text-muted d-block">
                            <Icon.Clock size={11} className="me-1" />
                            Up {fmtUptime(s.uptime)}
                          </small>
                        ) : null}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      color="warning"
                      outline
                      disabled={busyKey === `svc:${s.key}` || !!restarting[s.key]}
                      onClick={() =>
                        askPassword({
                          type: 'service',
                          payload: { key: s.key, action: 'restart' },
                          busyKey: `svc:${s.key}`,
                          title: `Restart ${s.name}`,
                          warning: `Restarting ${s.name} can interrupt live calls / data briefly.`
                        })
                      }>
                      {busyKey === `svc:${s.key}` || restarting[s.key] ? (
                        <Icon.Loader size={14} />
                      ) : (
                        <Icon.RotateCw size={14} />
                      )}
                      <span className="ms-1">Restart</span>
                    </Button>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>

          {/* Software services — single "Restart All" control (runs pm2 restart all) */}
          <Card className="p-3 mt-3 shadow-sm border-0">
            <h6 className="fw-bolder mb-3 d-flex align-items-center gap-2">
              <Icon.Box size={18} /> Software Services
            </h6>
            <div>
              <Button
                color="primary"
                disabled={busyKey === 'pm2:all'}
                onClick={() =>
                  askPassword({
                    type: 'pm2-all',
                    payload: {},
                    busyKey: 'pm2:all',
                    title: 'Restart All Software Services',
                    warning:
                      'This will restart ALL software services at once (including the dashboard API, which will reconnect in a few seconds).'
                  })
                }>
                {busyKey === 'pm2:all' ? <Icon.Loader size={16} /> : <Icon.RotateCw size={16} />}
                <span className="ms-2">Restart All</span>
              </Button>
            </div>
          </Card>

          {/* Top processes */}
          <Row className="g-3 mt-0">
            <Col lg="6">
              <Card className="p-3 shadow-sm border-0">
                <h6 className="fw-bolder mb-3 d-flex align-items-center gap-2">
                  <Icon.TrendingUp size={18} /> Top Processes by CPU
                </h6>
                <table className="report-list-table w-100">
                  <thead>
                    <tr>
                      <th>PID</th>
                      <th>Command</th>
                      <th>CPU%</th>
                      <th>MEM%</th>
                      <th>RSS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {processes.byCpu.map((p) => (
                      <tr key={p.pid}>
                        <td>{p.pid}</td>
                        <td className="fw-medium">{p.command}</td>
                        <td>{p.cpu}</td>
                        <td>{p.mem}</td>
                        <td>{fmtBytes(p.rss)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </Col>
            <Col lg="6">
              <Card className="p-3 shadow-sm border-0">
                <h6 className="fw-bolder mb-3 d-flex align-items-center gap-2">
                  <Icon.Server size={18} /> Top Processes by Memory
                </h6>
                <table className="report-list-table w-100">
                  <thead>
                    <tr>
                      <th>PID</th>
                      <th>Command</th>
                      <th>MEM%</th>
                      <th>CPU%</th>
                      <th>RSS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {processes.byMem.map((p) => (
                      <tr key={p.pid}>
                        <td>{p.pid}</td>
                        <td className="fw-medium">{p.command}</td>
                        <td>{p.mem}</td>
                        <td>{p.cpu}</td>
                        <td>{fmtBytes(p.rss)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Password confirmation modal for control actions */}
      <Modal isOpen={!!pendingAction} toggle={() => !pwSubmitting && setPendingAction(null)} centered>
        <ModalHeader toggle={() => !pwSubmitting && setPendingAction(null)}>
          {pendingAction?.title || 'Confirm Action'}
        </ModalHeader>
        <ModalBody>
          {pendingAction?.warning && (
            <div className="alert alert-warning py-2 small mb-3">
              <Icon.AlertTriangle size={15} className="me-1" />
              {pendingAction.warning}
            </div>
          )}
          <Label className="fw-medium">Control Password</Label>
          <Input
            type="password"
            autoFocus
            value={pwInput}
            placeholder="Enter control password"
            onChange={(e) => setPwInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runPendingAction()}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline disabled={pwSubmitting} onClick={() => setPendingAction(null)}>
            Cancel
          </Button>
          <Button color="primary" disabled={!pwInput || pwSubmitting} onClick={runPendingAction}>
            {pwSubmitting ? 'Please wait…' : 'Confirm'}
          </Button>
        </ModalFooter>
      </Modal>

    </div>
  );
};

export default ServerHealth;
